<template>
  <div class="px-4 pb-1" v-if="!pickerExpanded">
    {{ $t("signup.choose_company_country") }}:
    {{ getCountryLabel(localAccountData.country) }}
    <button @click="togglePicker" class="btn btn-link p-0">
      ({{ $t("common.edit") }})
    </button>
  </div>
  <div v-if="pickerExpanded" class="card p-4 mb-3">
    <h3 class="mb-1 mb-sm-2">
      {{ $t("signup.choose_company_country") }}
    </h3>
    <SearchableSelectComponent
      :options="countries"
      v-model="localAccountData.country"
      :placeholder="$t('signup.select_country_placeholder')"
      @update:modelValue="handleCountrySelected"
    />
  </div>
</template>

<script setup lang="ts">
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import {
  isAdminAccount,
  loadAccountForGate,
} from "@/composables/useAdminCheck";
import { ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants";
import { useSignupStore } from "@/signup/signup";
import { differenceBy, intersectionBy, lowerCase, sortBy } from "lodash-es";
import { storeToRefs } from "pinia";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const { country: selectedCountry, account } = storeToRefs(useSignupStore());

const localAccountData = reactive({ country: selectedCountry.value });
const router = useRouter();

const supportedCountries = sortBy(
  intersectionBy(ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS, "value"),
  ({ label }) => label
);

const unsupportedCountries = sortBy(
  differenceBy(ALL_COUNTRIES, SUPPORTED_COUNTRIES_WITH_LABELS, "value"),
  ({ label }) => label
);

// Format countries for SearchableSelectComponent (no disabled property needed)
const countries = [...supportedCountries, ...unsupportedCountries];

// Picker is expanded if locale is "en" (default) and collapsed if locale is "et" or "lv"
const { locale } = useI18n();
const pickerExpanded = ref(locale.value == "en" ? true : false);

const togglePicker = () => {
  pickerExpanded.value = !pickerExpanded.value;
};

/**
 * Get the display label for a country code
 */
const getCountryLabel = (countryCode: string): string => {
  if (!countryCode) return "";
  const country = countries.find((c) => c.value === countryCode);
  return country ? country.label : countryCode;
};

/**
 * Handle country selection from the SearchableSelectComponent
 */
const handleCountrySelected = async (countryCode: string) => {
  if (!countryCode) return;

  localAccountData.country = countryCode;
  await handleUpdateCountry();
};

const handleUpdateCountry = async () => {
  const country = localAccountData.country;

  // FI's deprecated dedicated flow is kept only for admins (per-country admin behavior is
  // unchanged). Non-admins selecting FI fall through to /signup/producer, where the flow
  // selector routes supported non-EE countries to the simplified international signup.
  if (country == "FI") {
    // The picker is also rendered on /signup/fi (FIFlowSelection), where the signup draft isn't
    // loaded: load the logged-in account there, so an admin isn't silently routed as a non-admin.
    const routingAccount = account.value.id
      ? account.value
      : await loadAccountForGate();
    if (isAdminAccount(routingAccount)) {
      // ?country=... is here so CountryPickerComponent can figure out what country to show
      await router.push(
        `/signup/${lowerCase(country)}?country=${lowerCase(country)}`
      );
      // Reload the page to ensure the new country is reflected, since the state store is currently poorly implemented
      router.go(0);
      return;
    }
  }

  await router.push(`/signup/producer?country=${lowerCase(country)}`);
  router.go(0);
};
</script>

<style scoped lang="scss"></style>
